import type { BlisterSettings } from "../types/settings";

export const DEFAULT_DESPAWN_Z = 5;
export const DEFAULT_SPAWN_Z = -30;

export const DEFAULT_BLISTER_SETTINGS: BlisterSettings = {
  colors: ["#C084FC", "#9333EA", "#7C3AED", "#4F46E5", "#3B82F6", "#0EA5E9"],
  particleSize: 0.55,
  speed: 0.08,
  count: 2600,
  opacity: 0.82,
  additive: true,
  speedRandomness: 0.7,
  sizeRandomness: 1.1,
  glowStrength: 7,
  blurStrength: 6.5,
  glowFraction: 0.24,
  blurFraction: 0.38,
  windStrength: 0.55,
  windFraction: 0.6,
  atmosphereIntensity: 0.68,
  ambientDriftSpeed: 0.42,
  hazeAmount: 0.58,
  fieldDistortion: 0.52,
  depthSoftness: 0.66,
  sparkleCount: 700,
  sparkleSize: 0.28,
  sparkleOpacity: 0.42,
  sparkleReactivity: 0.58,
  sparkleRandomness: 0.76,
  pulseIntensity: 0.14,
  pulseFrequency: 0.18,
  trailAmount: 0.12,
  afterimageAmount: 0.1,
  flashSoftness: 0.84,
  bloomIntensity: 0.78,
  bloomRadius: 0.56,
  grainAmount: 0.05,
  vignetteAmount: 0.3,
  chromaticAberration: 0.08,
  despawnZ: DEFAULT_DESPAWN_Z,
  spawnZ: DEFAULT_SPAWN_Z,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number, digits = 2) {
  return Number(value.toFixed(digits));
}

function randomBetween(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function randomInt(min: number, max: number) {
  return Math.round(randomBetween(min, max));
}

function hslToHex(h: number, s: number, l: number) {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hue = h / 60;
  const x = chroma * (1 - Math.abs((hue % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue >= 0 && hue < 1) {
    red = chroma;
    green = x;
  } else if (hue < 2) {
    red = x;
    green = chroma;
  } else if (hue < 3) {
    green = chroma;
    blue = x;
  } else if (hue < 4) {
    green = x;
    blue = chroma;
  } else if (hue < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  const match = lightness - chroma / 2;
  const toHex = (channel: number) =>
    Math.round((channel + match) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
}

function createCuratedPalette() {
  const baseHue = Math.random() * 360;
  const arc = randomBetween(18, 62);
  const direction = Math.random() > 0.5 ? 1 : -1;
  const saturation = randomBetween(45, 82);
  const lightnessFloor = randomBetween(36, 50);
  const lightnessPeak = randomBetween(64, 78);
  const palette = Array.from({ length: 6 }, (_, index) => {
    const step = index / 5;
    const hue = (baseHue + direction * (step - 0.5) * arc) % 360;
    const sat = clamp(saturation + randomBetween(-10, 8), 32, 88);
    const light = lightnessFloor + (lightnessPeak - lightnessFloor) * Math.sin(step * Math.PI);
    return hslToHex((hue + 360) % 360, sat, light + randomBetween(-4, 4));
  });

  return Array.from(new Set(palette)).slice(0, 6).concat(DEFAULT_BLISTER_SETTINGS.colors).slice(0, 6);
}

export function normalizeBlisterSettings(
  input?: Partial<BlisterSettings> | null
): BlisterSettings {
  const merged = {
    ...DEFAULT_BLISTER_SETTINGS,
    ...(input ?? {}),
  } as BlisterSettings;

  const count = clamp(Math.round(merged.count), 100, 5000);
  const bloomIntensity = clamp(merged.bloomIntensity, 0, 2.2);
  const grainAmount = clamp(merged.grainAmount, 0, bloomIntensity > 1.6 ? 0.14 : 0.22);
  const trailAmount = clamp(merged.trailAmount, 0, bloomIntensity > 1.5 ? 0.32 : 0.5);
  const afterimageAmount = clamp(
    merged.afterimageAmount,
    0,
    trailAmount > 0.34 ? 0.3 : 0.42
  );
  const sparkleCountMax = count > 3800 ? 1600 : count > 2200 ? 2200 : 3200;

  return {
    colors:
      Array.isArray(merged.colors) && merged.colors.length >= 6
        ? merged.colors.slice(0, 6)
        : DEFAULT_BLISTER_SETTINGS.colors,
    particleSize: round(clamp(merged.particleSize, 0.1, 1.8)),
    speed: round(clamp(merged.speed, 0.01, 0.24), 3),
    count,
    opacity: round(clamp(merged.opacity, 0.2, 1)),
    additive: merged.additive ?? true,
    speedRandomness: round(clamp(merged.speedRandomness, 0, 2.4)),
    sizeRandomness: round(clamp(merged.sizeRandomness, 0, 3)),
    glowStrength: round(clamp(merged.glowStrength, 0, 20), 1),
    blurStrength: round(clamp(merged.blurStrength, 0, 20), 1),
    glowFraction: round(clamp(merged.glowFraction, 0.02, 1)),
    blurFraction: round(clamp(merged.blurFraction, 0.02, 1)),
    windStrength: round(clamp(merged.windStrength, 0, 2)),
    windFraction: round(clamp(merged.windFraction, 0, 1)),
    atmosphereIntensity: round(clamp(merged.atmosphereIntensity, 0, 1.4)),
    ambientDriftSpeed: round(clamp(merged.ambientDriftSpeed, 0, 1.2)),
    hazeAmount: round(clamp(merged.hazeAmount, 0, 1.2)),
    fieldDistortion: round(clamp(merged.fieldDistortion, 0, 1.2)),
    depthSoftness: round(clamp(merged.depthSoftness, 0, 1.2)),
    sparkleCount: clamp(Math.round(merged.sparkleCount), 120, sparkleCountMax),
    sparkleSize: round(clamp(merged.sparkleSize, 0.08, 0.8)),
    sparkleOpacity: round(clamp(merged.sparkleOpacity, 0.06, 0.9)),
    sparkleReactivity: round(clamp(merged.sparkleReactivity, 0, 1.2)),
    sparkleRandomness: round(clamp(merged.sparkleRandomness, 0, 1.2)),
    pulseIntensity: round(clamp(merged.pulseIntensity, 0, 0.8)),
    pulseFrequency: round(clamp(merged.pulseFrequency, 0, 0.85)),
    trailAmount: round(trailAmount),
    afterimageAmount: round(afterimageAmount),
    flashSoftness: round(clamp(merged.flashSoftness, 0.1, 1)),
    bloomIntensity: round(bloomIntensity),
    bloomRadius: round(clamp(merged.bloomRadius, 0, 1)),
    grainAmount: round(grainAmount),
    vignetteAmount: round(clamp(merged.vignetteAmount, 0, 1)),
    chromaticAberration: round(clamp(merged.chromaticAberration, 0, 0.35)),
    despawnZ: merged.despawnZ ?? DEFAULT_DESPAWN_Z,
    spawnZ: merged.spawnZ ?? DEFAULT_SPAWN_Z,
  };
}

export function randomizeBlisterSettings(): BlisterSettings {
  const mistiness = randomBetween(0.35, 0.92);
  const density = randomBetween(0.25, 0.88);
  const brightness = randomBetween(0.4, 0.9);
  const bloomIntensity = randomBetween(0.65, 1.45);
  const count = randomInt(600 + density * 900, 2400 + density * 2600);
  const restrainedPulse = bloomIntensity > 1.2 ? 0.24 : 0.34;
  const trailAmount = randomBetween(0.04, 0.26) * (1 - mistiness * 0.16);
  const settings: Partial<BlisterSettings> = {
    colors: createCuratedPalette(),
    particleSize: randomBetween(0.24, 1.1),
    speed: randomBetween(0.03, 0.14),
    count,
    opacity: randomBetween(0.62, 0.96),
    additive: Math.random() > 0.18,
    speedRandomness: randomBetween(0.2, 1.3),
    sizeRandomness: randomBetween(0.5, 2.1),
    glowStrength: randomBetween(4.5, 15.5) * brightness,
    blurStrength: randomBetween(3.5, 12) * mistiness,
    glowFraction: randomBetween(0.12, 0.42),
    blurFraction: randomBetween(0.18, 0.55),
    windStrength: randomBetween(0.15, 1),
    windFraction: randomBetween(0.3, 0.85),
    atmosphereIntensity: randomBetween(0.38, 0.98),
    ambientDriftSpeed: randomBetween(0.12, 0.72),
    hazeAmount: randomBetween(0.22, 0.88),
    fieldDistortion: randomBetween(0.15, 0.9),
    depthSoftness: randomBetween(0.35, 0.95),
    sparkleCount: randomInt(360, count > 6500 ? 1200 : 2200),
    sparkleSize: randomBetween(0.14, 0.42),
    sparkleOpacity: randomBetween(0.16, 0.58),
    sparkleReactivity: randomBetween(0.22, 0.92),
    sparkleRandomness: randomBetween(0.32, 1),
    pulseIntensity: randomBetween(0.02, restrainedPulse),
    pulseFrequency: randomBetween(0.05, 0.45),
    trailAmount,
    afterimageAmount: trailAmount * randomBetween(0.18, 0.52),
    flashSoftness: randomBetween(0.62, 0.96),
    bloomIntensity,
    bloomRadius: randomBetween(0.35, 0.88),
    grainAmount: randomBetween(0.02, bloomIntensity > 1.3 ? 0.1 : 0.16),
    vignetteAmount: randomBetween(0.16, 0.54),
    chromaticAberration: randomBetween(0.02, 0.18),
  };

  return normalizeBlisterSettings(settings);
}